import { Chip, Grid, makeStyles, Typography } from "@material-ui/core";
import { useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import CancelIcon from "@material-ui/icons/Cancel";
import _ from "lodash";
import { Button, useDataProvider } from "react-admin";
import DragIcon from "assets/images/drag-icon.svg";
import CustomTooltip from "components/CustomTooltip";
import styled from "styled-components";
import ManageStudentsButton from "./ManageStudentsButton";
import ChangePickupLocationButton from "./ChangePickupLocationButton";
import { useWindowDimensions } from "utils/dimensionHook";
import PopoverActionButton from "./PopoverActionButton";
import ArrivedAtInput from "./ArrivedAtInput";
import RemarkLocationInput from "./RemarkLocationInput";

interface IDraggableComponent {
  selectedMarkers: any;
  parentCallback: (x: any) => void;
  parentChangeLocationCallback: (x: any, y: string) => void;
  parentManageStudentCallback: (x: any, y: any) => void;
  waveType: string;
  schoolData: ISchool;
  busScheduleType: string;
  routesData: any;
}

export const getLocationWithoutSchool = (waveType: string, markers: any) => {
  const getSchool = (waveType: string) => {
    if (waveType === "to_school") {
      return _.last(markers);
    }

    if (waveType === "from_school") {
      return _.first(markers);
    }
  };

  return _.without(markers, getSchool(waveType));
};

const useStyles = makeStyles({
  dangerButton: {
    color: "#DA4040",
  },
  dragButton: {
    width: "20px",
    height: "45px",
    margin: "13px 10px 10px 10px",
  },
  actionButtonWrapper: {
    paddingTop: 20,
    paddingRight: 6,
    display: "flex",
    justifyContent: "flex-end",
  },
  actionButtonWrapperSmallScreen: {
    paddingTop: 20,
  },
  dragContent: {
    display: "flex",
  },
  dragContentTitleText: {
    fontSize: "13pt",
    fontWeight: 500,
    color: "#000000",
    marginTop: 23,
  },
  dragContentTitleTextSmallScreen: {
    fontSize: "11pt",
    fontWeight: 500,
    color: "#000000",
    marginTop: 23,
  },
  removeLocationButton: {
    padding: "5px",
    marginLeft: "5px",
    minWidth: "10px",
    backgroundColor: "#DA4040",
    boxShadow: "0 4px 8px rgb(218 64 64 / 30%)",
    "& .MuiButton-textSizeSmall": {
      padding: 0,
    },
    "& .MuiButton-label > .MuiSvgIcon-root": {
      fontSize: 20,
    },
    "&:hover": {
      backgroundColor: "#DA4040 !important",
    },
  },
  removeLocationTooltip: {
    backgroundColor: "#DA4040 !important",
    marginTop: "-15px",
  },
  studentChipBox: {
    paddingLeft: 10,
  },
  studentChip: {
    margin: 5,
    backgroundColor: "#FFFFFF",
    color: "#6B6B6B",
    border: "1px solid #E3E3E3",
    borderRadius: 5,
  },
  studentChipWrapper: {
    marginBottom: 13,
  },
  arriveInputWrapper: {
    padding: "0px 5px",
  },
});

const DraggableComponent = ({
  selectedMarkers,
  parentCallback,
  parentChangeLocationCallback,
  parentManageStudentCallback,
  waveType,
  schoolData,
  busScheduleType,
  routesData,
}: IDraggableComponent) => {
  const [tempMarkers, setTempMarkers] = useState<any>([]);
  const [levels, setLevels] = useState<ILevel[]>([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState<number[]>([]);

  const classes = useStyles();
  const provider = useDataProvider();
  const { width } = useWindowDimensions();

  useEffect(() => {
    const withoutSchool = getLocationWithoutSchool(waveType, selectedMarkers);

    setTempMarkers(withoutSchool);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMarkers]);

  useEffect(() => {
    if (tempMarkers.length > 0) {
      getLevels(tempMarkers).then((result: any) => {
        setLevels(result);
      });

      let tempSelectedStudentIds: any = [];
      tempMarkers.forEach((tempMarker: any) => {
        const selectedStudentIds = tempMarker.students.map(
          (student: IStudent) => {
            return student.id;
          },
        );
        tempSelectedStudentIds = [
          ...tempSelectedStudentIds,
          ...selectedStudentIds,
        ];
      });

      setSelectedStudentIds(tempSelectedStudentIds);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempMarkers]);

  const getLevels = async (selectedMarkers: any) => {
    let temp: any = [];

    selectedMarkers.forEach((selectedMarker: any) => {
      const levelIds = selectedMarker.students.map((student: IStudent) => {
        return student.levelId;
      });
      temp = [...temp, ...levelIds];
    });

    return await provider.getMany("levels", { ids: temp }).then((result) => {
      return result.data;
    });
  };

  const getItemStyle = (isDragging: any, draggableStyle: any) => ({
    // some basic styles to make the items look a bit nicer
    // userSelect: "none",
    margin: "0 0 20px 0",
    border: "1px solid #EAEAEA",

    // change background colour if dragging
    background: isDragging ? "#FBFBFB" : "#FFFFFF",

    // styles we need to apply on draggables
    ...draggableStyle,
  });

  const Item = styled.div`
    &:hover {
      box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.08);
    },
  `;

  const getListStyle = (isDraggingOver: any) => ({
    background: isDraggingOver ? "#FBFBFB" : "#FBFBFB",
    width: "100%",
  });

  // a little function to help us with reordering the result
  const reorder = (list: any, startIndex: any, endIndex: any) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const onDragEnd = (result: any) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }

    const items = reorder(
      tempMarkers,
      result.source.index,
      result.destination.index,
    );

    setTempMarkers(items);

    parentCallback && parentCallback(items);
  };

  const removeLocationList = (order: number) => {
    const items = tempMarkers;
    const findData = _.find(items, ["order", order]);

    if (_.isEmpty(findData)) {
      return;
    }

    _.pullAllWith(items, [findData], _.isEqual);

    setTempMarkers(items);

    parentCallback && parentCallback(items);
  };

  const handleManageStudentCallback = (newLocationValue: any) => {
    const items = tempMarkers;
    const indexData = _.findIndex(items, ["order", newLocationValue.order]);

    if (newLocationValue.students.length !== 0) {
      items[indexData] = newLocationValue;
    } else {
      const findData = _.find(items, ["order", newLocationValue.order]);

      _.pullAllWith(items, [findData], _.isEqual);
    }

    setTempMarkers(items);

    parentManageStudentCallback &&
      parentManageStudentCallback(items, newLocationValue);
    parentCallback && parentCallback(items);
  };

  const handleChangeLocationCallback = (
    newLocationValue: any,
    oldLocationValue: any,
  ) => {
    const items = tempMarkers;
    const indexData = _.findIndex(items, ["order", oldLocationValue.order]);

    items[indexData] = newLocationValue;

    setTempMarkers(items);

    parentChangeLocationCallback &&
      parentChangeLocationCallback(newLocationValue, oldLocationValue);
    parentCallback && parentCallback(items);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable">
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            style={getListStyle(snapshot.isDraggingOver)}
          >
            {tempMarkers.length > 0}
            {
              <>
                {tempMarkers.map((item: any, index: number) => {
                  item.order = index + 1;

                  // convert ASCII Code into Alphabet
                  // 65 === A
                  const locationCode = String.fromCharCode(65 + index);

                  return (
                    <Draggable
                      key={`draggable-${index}`}
                      draggableId={`draggable-${index}`}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <Item
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={getItemStyle(
                            snapshot.isDragging,
                            provided.draggableProps.style,
                          )}
                        >
                          <Grid container>
                            <Grid item xs={12}>
                              <Grid container>
                                <Grid
                                  item
                                  xs={width <= 1024 ? 10 : 8}
                                  className={classes.dragContent}
                                >
                                  <div {...provided.dragHandleProps}>
                                    <img
                                      className={classes.dragButton}
                                      src={DragIcon}
                                      alt="Drag"
                                    />
                                  </div>
                                  <Typography
                                    className={
                                      width <= 1024
                                        ? classes.dragContentTitleTextSmallScreen
                                        : classes.dragContentTitleText
                                    }
                                  >
                                    {item.blockNumber} {item.street}{" "}
                                    {item.postalCode}
                                  </Typography>
                                </Grid>
                                <Grid
                                  item
                                  xs={width <= 1024 ? 2 : 4}
                                  className={
                                    width <= 1024
                                      ? classes.actionButtonWrapperSmallScreen
                                      : classes.actionButtonWrapper
                                  }
                                >
                                  {width <= 1024 ? (
                                    <PopoverActionButton
                                      routeLocationData={item}
                                      schoolData={schoolData}
                                      selectedStudentIds={selectedStudentIds}
                                      busScheduleType={busScheduleType}
                                      manageStudentCallback={
                                        handleManageStudentCallback
                                      }
                                      changeLocationCallback={
                                        handleChangeLocationCallback
                                      }
                                      removeLocationListCallback={
                                        removeLocationList
                                      }
                                      routesData={routesData}
                                      selectedMarkers={selectedMarkers}
                                    />
                                  ) : (
                                    <>
                                      <ManageStudentsButton
                                        routeLocationData={item}
                                        schoolData={schoolData}
                                        selectedStudentIds={selectedStudentIds}
                                        manageStudentCallback={
                                          handleManageStudentCallback
                                        }
                                        busScheduleType={busScheduleType}
                                        routesData={routesData}
                                        selectedMarkers={selectedMarkers}
                                      />

                                      <ChangePickupLocationButton
                                        routeLocationData={item}
                                        changeLocationCallback={
                                          handleChangeLocationCallback
                                        }
                                      />

                                      <CustomTooltip
                                        title="Remove Location"
                                        aria-label="Remove Location"
                                        classes={{
                                          tooltip:
                                            classes.removeLocationTooltip,
                                        }}
                                      >
                                        <Button
                                          className={
                                            classes.removeLocationButton
                                          }
                                          onClick={() =>
                                            removeLocationList(item.order)
                                          }
                                        >
                                          <CancelIcon />
                                        </Button>
                                      </CustomTooltip>
                                    </>
                                  )}
                                </Grid>
                              </Grid>
                            </Grid>

                            <Grid
                              item
                              xs={12}
                              className={classes.arriveInputWrapper}
                            >
                              <ArrivedAtInput routeLocationData={item} />
                            </Grid>

                            <Grid
                              item
                              xs={12}
                              className={classes.arriveInputWrapper}
                            >
                              <RemarkLocationInput routeLocationData={item} />
                            </Grid>

                            <Grid
                              item
                              xs={12}
                              className={classes.studentChipWrapper}
                            >
                              {item.students
                                .sort((a: any, b: any) => a.id - b.id)
                                .map((student: IStudent, index: number) => {
                                  student.locationCode = `${locationCode}${
                                    index + 1
                                  }`;

                                  return (
                                    <Chip
                                      key={`student-key-${index}`}
                                      label={`
                                      ${student.locationCode} - 
                                      ${_.truncate(
                                        `${student.firstName} ${student.lastName}`,
                                        {
                                          length: 15,
                                        },
                                      )} - ${
                                        levels.length > 0 &&
                                        _.find(levels, ["id", student.levelId])
                                          ?.name
                                      }`}
                                      className={classes.studentChip}
                                    />
                                  );
                                })}
                            </Grid>
                          </Grid>
                        </Item>
                      )}
                    </Draggable>
                  );
                })}
              </>
            }

            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default DraggableComponent;
